import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import {
  inputVariants,
  type InputVariants,
} from "../../DesignSystem/index.styles";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "../../ui/button-group";
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
} from "../../ui/input-group";
import { Minus, Plus } from "lucide-react";

interface BaseInputProps extends InputVariants {
  label?: string;
  error?: string;
  className?: string;
  placeholder?: string;
  name?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  type?: "text" | "number" | "email" | "tel";
}

interface NormalInputProps extends BaseInputProps {
  variant: "normal";
}

interface IncrementInputProps extends BaseInputProps {
  variant: "increment";
  onIncrement?: () => void;
  onDecrement?: () => void;
  min?: number;
  max?: number;
}

interface SuffixInputProps extends BaseInputProps {
  variant: "suffix";
  suffix: string;
}

type InputProps = NormalInputProps | IncrementInputProps | SuffixInputProps;

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { label, error, className, size, disabled, required, ...restProps } =
    props;

  const variantType = props.variant || "normal";
  const inputVariant = error ? "error" : "default";

  // Render based on variant
  const renderInput = () => {
    switch (variantType) {
      case "increment": {
        const {
          onIncrement,
          onDecrement,
          min = 0,
          max,
          value,
          onChange,
          ...inputProps
        } = props as IncrementInputProps;
        const numValue =
          typeof value === "string" ? parseInt(value || "0", 10) : value || 0;

        const handleIncrement = () => {
          if (max !== undefined && numValue >= max) return;
          onIncrement?.();
        };

        const handleDecrement = () => {
          if (numValue <= min) return;
          onDecrement?.();
        };

        return (
          <ButtonGroup className="w-full min-w-0">
            <Button
              type="button"
              onClick={handleDecrement}
              disabled={disabled || numValue <= min}
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0 rounded-r-none border border-gray-300"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <input
              ref={ref}
              type="number"
              value={value}
              onChange={onChange}
              disabled={disabled}
              className={cn(
                "flex-1 h-11 min-w-0 text-center bg-gray-50 border border-gray-300 border-l-0 border-r-0 px-3 relative z-0",
                "focus:outline-none focus:ring-2 focus:ring-prime-700 focus:ring-inset focus-visible:z-10",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                error && "border-red-500 focus:ring-red-500",
                className
              )}
              {...inputProps}
            />
            <Button
              type="button"
              onClick={handleIncrement}
              disabled={disabled || (max !== undefined && numValue >= max)}
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0 rounded-l-none border border-gray-300"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </ButtonGroup>
        );
      }

      case "suffix": {
        const { suffix, ...inputProps } = props as SuffixInputProps;
        return (
          <InputGroup>
            <InputGroupInput
              ref={ref}
              disabled={disabled}
              className={cn(
                "pr-12 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                error &&
                  "border-red-500 bg-white focus-visible:ring-red-500 focus-visible:border-red-500",
                className
              )}
              {...inputProps}
            />
            <InputGroupAddon align="inline-end">
              <span className="text-sm font-medium">{suffix}</span>
            </InputGroupAddon>
          </InputGroup>
        );
      }

      default: {
        return (
          <input
            ref={ref}
            disabled={disabled}
            className={cn(
              "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
              inputVariants({ size, variant: inputVariant }),
              className
            )}
            {...restProps}
          />
        );
      }
    }
  };

  return (
    <div className="w-full min-w-0">
      {label && (
        <Label className="block text-sm font-medium mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      {renderInput()}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
});

Input.displayName = "Input";

export default Input;
